import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Loader2, Plus, RefreshCw, X, CheckCircle2, Search, BadgeCheck, MapPin, PackageCheck } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { orgLabel } from "@/lib/constants";
import { toast } from "sonner";

const ROLES = ["supplier", "buyer", "investor", "lender", "contractor", "service_provider", "distributor", "partner"];

const STATUS = {
  matched: { label: "Network match ready", cls: "bg-[#DCFCE7] text-[#059669]" },
  insufficient_internal: { label: "Sourcing counterparts", cls: "bg-[#FEF3C7] text-[#D97706]" },
  open: { label: "Evaluating", cls: "bg-slate-100 text-slate-500" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-400" },
};

const EMPTY = {
  direction: "need", title: "", counterpart_role: "supplier", category: "",
  product_service: "", quantity: "", geography: "", verification_required: false,
};

export default function Needs() {
  const location = useLocation();
  const highlightId = new URLSearchParams(location.search).get("need");
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/needs").then(({ data }) => setNeeds(data.needs)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  // Deep-link from a bell notification (/needs?need=<id>): scroll the target Need into view + highlight it.
  useEffect(() => {
    if (!highlightId || loading || !needs.length) return;
    const el = document.querySelector(`[data-testid="need-card-${highlightId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId, loading, needs]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.product_service.trim()) { toast.error("Title and product/service are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/needs", form);
      const n = data.need;
      setNeeds((p) => [n, ...p.filter((x) => x.need_id !== n.need_id)]);
      setForm(EMPTY); setShowForm(false);
      toast.success(n.status === "matched"
        ? `Found ${n.recommendations.length} qualified counterpart(s) in the DEI network`
        : "No qualified counterpart yet — we're sourcing options for you");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const reevaluate = async (id) => {
    try {
      const { data } = await api.post(`/needs/${id}/reevaluate`);
      setNeeds((p) => p.map((n) => (n.need_id === id ? data.need : n)));
      toast.success("Re-evaluated against the current network");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const close = async (id) => {
    try { await api.post(`/needs/${id}/close`); setNeeds((p) => p.map((n) => (n.need_id === id ? { ...n, status: "closed" } : n))); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const connect = async (needId, profileId) => {
    try {
      await api.post(`/needs/${needId}/connect`, { profile_id: profileId });
      toast.success("Interest sent — track it under Connections");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not connect"); }
  };

  return (
    <AppShell title="My Needs & Opportunities" subtitle="Tell us what you need or offer — we search the verified DEI network first.">
      <div className="flex justify-end mb-6">
        <button onClick={() => setShowForm((s) => !s)} data-testid="new-need-button"
          className="h-11 px-5 rounded-md bg-[#2563EB] text-white font-medium flex items-center gap-2 hover:bg-[#1D4ED8] transition-colors">
          {showForm ? <X size={18} /> : <Plus size={18} />}{showForm ? "Cancel" : "New Need / Opportunity"}
        </button>
      </div>

      {showForm && (
        <motion.form initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
          data-testid="need-form" className="rounded-xl border border-slate-200 bg-white p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <label className="text-sm font-medium text-slate-600 sm:col-span-2">Title
            <input data-testid="need-title" value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Copper cathode — 50,000 lbs / month" className="mt-1 w-full h-11 px-3 rounded-md border border-slate-300" /></label>
          <label className="text-sm font-medium text-slate-600">I am
            <select data-testid="need-direction" value={form.direction} onChange={(e) => set("direction", e.target.value)}
              className="mt-1 w-full h-11 px-3 rounded-md border border-slate-300 bg-white">
              <option value="need">Looking for (a need)</option>
              <option value="offer">Offering (supplying)</option>
            </select></label>
          <label className="text-sm font-medium text-slate-600">Counterpart role
            <select data-testid="need-role" value={form.counterpart_role} onChange={(e) => set("counterpart_role", e.target.value)}
              className="mt-1 w-full h-11 px-3 rounded-md border border-slate-300 bg-white capitalize">
              {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select></label>
          <label className="text-sm font-medium text-slate-600 sm:col-span-2">Product / service &amp; specifications
            <textarea data-testid="need-product" value={form.product_service} onChange={(e) => set("product_service", e.target.value)}
              rows={2} placeholder="e.g. Grade-A copper cathode, 99.99% purity" className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300" /></label>
          <label className="text-sm font-medium text-slate-600">Category / industry
            <input data-testid="need-category" value={form.category} onChange={(e) => set("category", e.target.value)}
              placeholder="Metals" className="mt-1 w-full h-11 px-3 rounded-md border border-slate-300" /></label>
          <label className="text-sm font-medium text-slate-600">Quantity / capacity
            <input data-testid="need-quantity" value={form.quantity} onChange={(e) => set("quantity", e.target.value)}
              placeholder="50,000 lbs / month" className="mt-1 w-full h-11 px-3 rounded-md border border-slate-300" /></label>
          <label className="text-sm font-medium text-slate-600">Geography
            <input data-testid="need-geography" value={form.geography} onChange={(e) => set("geography", e.target.value)}
              placeholder="Houston, TX" className="mt-1 w-full h-11 px-3 rounded-md border border-slate-300" /></label>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600 sm:col-span-2">
            <input type="checkbox" data-testid="need-verification-required" checked={form.verification_required}
              onChange={(e) => set("verification_required", e.target.checked)} className="h-4 w-4" />
            Only recommend verified counterparts
          </label>
          <div className="sm:col-span-2">
            <button type="submit" disabled={busy} data-testid="submit-need"
              className="h-11 px-6 rounded-md bg-[#0A0F1C] text-white font-medium flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />} Search DEI network
            </button>
          </div>
        </motion.form>
      )}

      {loading ? (
        <div className="grid gap-4">{[1, 2].map((i) => <div key={i} className="h-40 rounded-xl border border-slate-200 bg-white animate-pulse" />)}</div>
      ) : needs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="needs-empty">
          <Target className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-medium text-[#0A0F1C]">No needs yet</p>
          <p className="text-sm text-slate-500 mt-1">Post what you need or offer — we evaluate qualified DEI members first.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {needs.map((n) => {
            const st = STATUS[n.status] || STATUS.open; const ev = n.evaluation || {};
            return (
              <div key={n.need_id} data-testid={`need-card-${n.need_id}`}
                className={`rounded-xl border bg-white p-5 transition-all ${highlightId === n.need_id ? "border-[#2563EB] ring-2 ring-[#2563EB]/30" : "border-slate-200"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-[#0A0F1C]">{n.title}</h3>
                      {n.title_translated && <span className="text-xs italic text-slate-400">🌐 {n.title_translated}</span>}
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`need-status-${n.need_id}`}>{st.label}</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      <span className="capitalize">{n.direction === "need" ? "Seeking" : "Offering"}</span> · {n.product_service}
                      {n.geography && <> · {n.geography}</>}{n.quantity && <> · {n.quantity}</>}
                    </p>
                    {ev.candidates_evaluated != null && (
                      <p className="text-xs text-slate-400 mt-1">
                        Evaluated {ev.candidates_evaluated} network members · {ev.qualified_count} qualified · {ev.strong_count} strong fit
                      </p>
                    )}
                  </div>
                  {n.status !== "closed" && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => reevaluate(n.need_id)} data-testid={`need-reevaluate-${n.need_id}`}
                        className="h-8 px-2.5 rounded border border-slate-200 text-xs text-slate-600 flex items-center gap-1 hover:bg-slate-50"><RefreshCw size={13} /> Re-check</button>
                      <button onClick={() => close(n.need_id)} data-testid={`need-close-${n.need_id}`}
                        className="h-8 px-2.5 rounded border border-slate-200 text-xs text-slate-500 flex items-center gap-1 hover:bg-slate-50"><X size={13} /> Close</button>
                    </div>
                  )}
                </div>

                {n.recommendations && n.recommendations.length > 0 ? (
                  <div className="mt-4 border-t border-slate-100 pt-4 space-y-2">
                    {n.recommendations.map((r) => (
                      <div key={r.profile_id} data-testid={`need-rec-${r.profile_id}`} className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                        <div className="h-10 w-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center shrink-0">
                          <span className="font-mono text-sm font-bold text-[#059669]">{r.score}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-[#0A0F1C] truncate">{r.org_name}</span>
                            {r.verified && <BadgeCheck size={14} className="text-[#2563EB] shrink-0" />}
                            <span className="text-[10px] uppercase tracking-wide text-slate-400">{orgLabel(r.org_type)}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{r.reason}</p>
                          {r.capacity_confirmed && (
                            <span data-testid={`capacity-badge-${r.profile_id}`}
                              className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#059669]"
                              title={r.capacity_detail ? `${r.capacity_detail.quantity} ${r.capacity_detail.unit} · ${r.capacity_detail.authority}` : ""}>
                              <PackageCheck size={11} /> Capacity Confirmed by Business
                            </span>
                          )}
                          {r.capacity_note && !r.capacity_confirmed && <p className="text-[11px] text-[#D97706] mt-0.5">⚠ {r.capacity_note}</p>}
                        </div>
                        {r.location && <span className="text-xs text-slate-400 flex items-center gap-1 shrink-0"><MapPin size={11} />{r.location}</span>}
                        <button onClick={() => connect(n.need_id, r.profile_id)} data-testid={`need-connect-${r.profile_id}`}
                          className="h-8 px-3 rounded-md bg-[#2563EB] text-white text-xs font-medium shrink-0 hover:bg-[#1D4ED8] transition-colors">Express interest</button>
                      </div>
                    ))}
                  </div>
                ) : n.status === "insufficient_internal" ? (
                  <div className="mt-4 border-t border-slate-100 pt-4 flex items-center gap-2 text-sm text-[#D97706]">
                    <Search size={15} /> No qualified DEI counterpart yet — recorded as a network gap; our team may recruit a verified provider for you.
                  </div>
                ) : n.status === "matched" && (
                  <div className="mt-4 border-t border-slate-100 pt-4 flex items-center gap-2 text-sm text-[#059669]">
                    <CheckCircle2 size={15} /> Qualified counterparts identified in the DEI network.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
